import { Box, Flex, Radio, RadioGroup, Text, Tooltip } from '@chakra-ui/react';
import {
  faArrowsRotate,
  faSearch,
  faSliders,
  faXmark
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Badge,
  Button,
  Modal,
  SearchInput,
  useToast
} from '@morressier/alembic';
import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useWorkspace } from '../../SubmissionContexts/Users/userHook';
import {
  fetchSubmissionsDashBoardConfig,
  fetchWorkspaceJournals,
  getExportHistoryByContext
} from 'src/api/queries';
import { ISubmissionContextJournal } from '@publishing-engine/domain/views';
import CreateConfiguration from 'src/components/VendorExport/CreateConfiguration';
import VendorExportHistoryTable from 'src/components/VendorExport/VendorExportHistoryTable';
import VendorExportConfigTableAdmin from 'src/components/VendorExport/VendorExportConfigTableAdmin';
import { NoRecordFound } from 'src/pages/ReviewList/EmptyState';
import { useForm } from 'react-hook-form';
import FilterDrawer from 'src/pages/Organization/public/Journal/SubmissionUpload/components/common/Filters/FilterDrawer';
import VendorExportCountBox from 'src/components/VendorExport/VendorExportCountBox';
import AdvanceConfigurationVendorExport from 'src/components/VendorExport/AdvanceConfigurationVendorExport';
import {
  getTodayFullDayRange,
  renderBadge,
  STATUS_FILTER_MAP
} from 'src/components/VendorExport/utils/VendorExportUtilities';
import Footer from 'src/components/Footer';
import { queryClient } from 'src/utils/queryClient';

const VendorExport = () => {
  const [journalSelectBoxOpen, setJournalSelectBoxOpen] =
    useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedJournal, setSelectedJournal] =
    useState<ISubmissionContextJournal>();
  const [searchValue, setSearchValue] = useState('');
  const [selectedFilters, setSelectedFilters] = useState<
    Record<string, string[]>
  >({});
  const [isAdvanceConfigurationModalOpen, setIsAdvanceConfigurationModalOpen] =
    useState(false);
  const [page, setPage] = useState(1);
  const LIMIT = 10;
  const offset = (page - 1) * LIMIT;
  const defaultValues = {
    searchParams: '',
    vendorNames: [],
    exportStatus: [],
    dateRange: [] as string[]
  };
  const toast = useToast();
  const [countLabel, setCountLabel] = useState('Total Exports');

  const { watch, setValue } = useForm<any>({
    defaultValues
  });
  const filters = watch();
  const { workspaceId, orgId } = useWorkspace();

  const { data, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [
        'submission-context-export-configs',
        { orgId, ctxId: selectedJournal?.id }
      ],
      queryFn: ({ pageParam = 0 }) =>
        workspaceId
          ? fetchWorkspaceJournals(workspaceId, pageParam, LIMIT)
          : null,
      enabled: !!workspaceId,
      getNextPageParam: (lastPage, allPages) => {
        if (!lastPage) return undefined;
        const maxOffset = lastPage.total;
        const nextOffset = allPages.length * LIMIT;
        return nextOffset < maxOffset ? nextOffset : undefined;
      }
    });
  const journals = useMemo(
    () => data?.pages?.flatMap(page => page?.collection ?? []) ?? [],
    [data?.pages]
  );
  const { data: ExportHistory } = useQuery({
    queryKey: [
      'submission-context-export-configs',
      { submissionContextId: selectedJournal?.id },
      filters,
      page
    ],
    queryFn: () =>
      selectedJournal?.id
        ? getExportHistoryByContext(
            selectedJournal?.id,
            orgId,
            filters,
            LIMIT,
            offset
          )
        : null,
    enabled: !!workspaceId
  });
  useEffect(() => {
    if (!selectedJournal || journals.length === 0) return;
    const updatedJournal = journals.find(j => j.id === selectedJournal.id);
    if (updatedJournal) {
      setSelectedJournal(updatedJournal);
    }
  }, [journals, selectedJournal?.id]);
  const filtersKey = useMemo(
    () =>
      JSON.stringify({
        vendorNames: filters.vendorNames ?? [],
        exportStatus: filters.exportStatus ?? [],
        dateRange: filters.dateRange ?? [],
        searchParams: (filters.searchParams ?? '').trim()
      }),
    [
      filters.vendorNames,
      filters.exportStatus,
      filters.dateRange,
      filters.searchParams
    ]
  );
  useEffect(() => {
    setPage(1);
  }, [filtersKey]);
  const view = 'vendor-exports';
  const { data: filtersData } = useQuery({
    queryKey: [
      'submission-dashboard-config',
      'submission-context-export-configs',
      { view, workspaceId, orgId }
    ],
    queryFn: () =>
      fetchSubmissionsDashBoardConfig({
        view,
        workspaceId,
        submissionContextId: selectedJournal?.id
      }),
    enabled: !!selectedJournal?.id
  });
  const handleFiltersSubmit = (selectedFilters: Record<string, string[]>) => {
    Object.entries(selectedFilters).forEach(([key, values]) => {
      setValue(key as string, values as any);
    });
  };
  const total = ExportHistory ? ExportHistory.total : 0;

  const totalPages = Math.ceil(total / LIMIT);

  // Card Filters
  const applyStatusFilter = (label: string) => {
    const status =
      STATUS_FILTER_MAP[label as 'success' | 'failed' | 'inProgress'];

    setValue('exportStatus', [status]);
    setSelectedFilters(prev => ({
      ...prev,
      exportStatus: [status]
    }));
  };
  const applyTodayDateFilter = (label: string) => {
    if (label === 'today') {
      const [start, end] = getTodayFullDayRange();
      setValue('dateRange', [start, end]);
      setSelectedFilters(prev => ({
        ...prev,
        dateRange: [start, end],
        exportStatus: []
      }));
    }
    if (label === 'total') {
      setValue('dateRange', []);
      setSelectedFilters(prev => ({
        ...prev,
        dateRange: []
      }));
    }
  };
  return (
    <Box
      id="scrollable-wrapper"
      overflowY="auto"
      overflowX="hidden"
      height="100%"
    >
      <Box height="100%">
        <Box padding={{ base: 2, md: 5 }} minHeight="100%">
          <Flex
            justifyContent="space-between"
            flexDirection={{ base: 'column', md: 'row' }}
            gap={{ base: 3, md: 0 }}
          >
            <Text textStyle="headingSection">Exports</Text>
            <Box position="relative" display="inline-block">
              <Button
                variant="secondary"
                onClick={() => setJournalSelectBoxOpen(prev => !prev)}
              >
                <Text
                  mr={2}
                  maxWidth={{ base: '180px', md: '260px', lg: 'none' }}
                  overflow={{ base: 'hidden', lg: 'visible' }}
                  textOverflow={{ base: 'ellipsis', lg: 'clip' }}
                  whiteSpace={{ base: 'nowrap', lg: 'normal' }}
                >
                  Journal: {selectedJournal?.data.name ?? 'Select journal'}
                </Text>
                <FontAwesomeIcon icon={faSliders} />
              </Button>

              {journalSelectBoxOpen && (
                <Box
                  position="absolute"
                  top="100%"
                  right={0}
                  mt={2}
                  zIndex={10}
                  bg="white"
                  borderRadius="md"
                  boxShadow="lg"
                  p={3}
                  minW="260px"
                  maxWidth={{ base: 'calc(100vw - 32px)', md: '320px' }}
                  maxH="450px"
                  overflowY="auto"
                >
                  <RadioGroup
                    value={selectedJournal?.data.shortname}
                    onChange={(shortname: string) => {
                      const journal = journals.find(
                        j => j.data.shortname === shortname
                      );

                      if (journal) {
                        setSelectedJournal(journal);
                        setJournalSelectBoxOpen(false);
                      }
                    }}
                  >
                    <Flex direction="column" gap={2}>
                      {journals.map(journal => (
                        <Radio
                          key={journal.data.shortname}
                          value={journal.data.shortname}
                        >
                          {journal.data.name}
                        </Radio>
                      ))}
                      {hasNextPage && (
                        <Button
                          variant="secondary"
                          onClick={() => fetchNextPage()}
                          isLoading={isFetchingNextPage}
                        >
                          Load More
                        </Button>
                      )}
                    </Flex>
                  </RadioGroup>
                </Box>
              )}
            </Box>
          </Flex>

          {selectedJournal ? (
            <>
              {/* Configuration Table */}
              <Flex direction="column" gap={3}>
                <Flex
                  justifyContent="space-between"
                  mt={3}
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={{ base: 3, md: 0 }}
                >
                  <Flex direction="column">
                    <Text textStyle="headingGroup">Configuration</Text>
                    <Text textStyle="textBody">
                      Configured daily data exports for vendor submissions.
                    </Text>
                  </Flex>
                  <Button onClick={() => setIsOpen(true)}>
                    Configure an Export
                  </Button>
                </Flex>
                {selectedJournal && (
                  <Box overflowX="auto" width="100%">
                    <VendorExportConfigTableAdmin
                      submissionContext={selectedJournal}
                    />
                  </Box>
                )}
              </Flex>
              {selectedJournal?.id && (
                <Modal
                  isOpen={isOpen}
                  onClose={() => {
                    setIsOpen(false);
                  }}
                  width={{ base: 'calc(100vw - 16px)', md: '720px' } as any}
                  closeButtonProps={{
                    hidden: false
                  }}
                >
                  <CreateConfiguration
                    submissionContextId={selectedJournal?.id}
                    setIsOpen={setIsOpen}
                    orgId={orgId}
                    isEdit={false}
                  />
                </Modal>
              )}
              {/* History Table */}
              <Flex flexDirection="column" gap={3} mt={5}>
                <Flex
                  justifyContent="space-between"
                  alignItems={{ base: 'flex-start', md: 'center' }}
                  flexDirection={{ base: 'column', md: 'row' }}
                  gap={{ base: 3, md: 0 }}
                >
                  <Flex direction="column">
                    <Text textStyle="headingGroup">History</Text>
                    <Text textStyle="textBody">
                      View a record of all past exports.
                    </Text>
                  </Flex>
                  <Flex gap={3}>
                    <Tooltip
                      label={'Refresh history'}
                      hasArrow
                      shouldWrapChildren
                      placement="top"
                    >
                      <Button
                        variant="secondary"
                        onClick={async () => {
                          try {
                            await queryClient.invalidateQueries({
                              queryKey: ['submission-context-export-configs']
                            });

                            toast({
                              variant: 'success',
                              title: 'History refreshed successfully'
                            });
                          } catch (error) {
                            toast({
                              variant: 'error',
                              title: 'Failed to refresh history',
                              description: 'Please try again.'
                            });
                          }
                        }}
                      >
                        <FontAwesomeIcon icon={faArrowsRotate} />
                      </Button>
                    </Tooltip>
                    <Button
                      onClick={() => setIsAdvanceConfigurationModalOpen(true)}
                    >
                      Advance Bulk Operation
                    </Button>
                  </Flex>
                </Flex>

                {selectedJournal && ExportHistory && (
                  <Flex
                    justifyContent="space-between"
                    gap={3}
                    mt={3}
                    flexWrap={{ base: 'wrap', md: 'nowrap' }}
                  >
                    <VendorExportCountBox
                      label="total"
                      count={ExportHistory.count.total}
                      onTodayClick={applyTodayDateFilter}
                      countLabel={countLabel}
                      setCountLabel={setCountLabel}
                    />
                    <VendorExportCountBox
                      label="inProgress"
                      count={ExportHistory.count.inProgress}
                      onStatusClick={applyStatusFilter}
                    />
                    <VendorExportCountBox
                      label="success"
                      count={ExportHistory.count.success}
                      onStatusClick={applyStatusFilter}
                    />
                    <VendorExportCountBox
                      label="failed"
                      count={ExportHistory.count.failed}
                      onStatusClick={applyStatusFilter}
                    />
                  </Flex>
                )}

                {selectedJournal && (
                  <>
                    <Flex
                      mt={5}
                      gap={2}
                      flexDirection={{ base: 'column', md: 'row' }}
                    >
                      <Flex gap={2} alignItems="center" flex="1">
                        <SearchInput
                          placeholder="Search submissions by manuscript ID"
                          iconLeft={<FontAwesomeIcon icon={faSearch} />}
                          width="100%"
                          name="searchParams"
                          value={searchValue}
                          enterKeyHint="search"
                          onChange={e => setSearchValue(e.currentTarget.value)}
                          onInputClear={() => {
                            setValue('searchParams', '');
                            setSearchValue('');
                          }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              setValue('searchParams', searchValue.trim());
                            }
                          }}
                          onKeyUp={e => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              setValue('searchParams', searchValue.trim());
                            }
                          }}
                        />
                        <Box
                          display={{ base: 'block', lg: 'none' }}
                          flexShrink={0}
                        >
                          <Button
                            variant="primary"
                            aria-label="Search"
                            onClick={() =>
                              setValue('searchParams', searchValue.trim())
                            }
                          >
                            <FontAwesomeIcon icon={faSearch} />
                          </Button>
                        </Box>
                      </Flex>

                      <FilterDrawer
                        filterGroups={
                          filtersData?.filters?.map((filter: any) => ({
                            ...filter,
                            items: filter.items ?? []
                          })) ?? []
                        }
                        watch={watch}
                        onSubmit={handleFiltersSubmit}
                        selectedFilters={selectedFilters}
                        setSelectedFilters={setSelectedFilters}
                      />
                    </Flex>
                    {Object.keys(filters).some(
                      key =>
                        Array.isArray(filters[key]) && filters[key].length > 0
                    ) && (
                      <Flex
                        alignItems="center"
                        gap={2}
                        wrap="wrap"
                        mb={2}
                        maxWidth="100%"
                        minWidth={0}
                      >
                        {(() => {
                          return (
                            <>
                              {Array.isArray(filters.dateRange) &&
                                filters.dateRange.length === 2 && (
                                  <Badge
                                    size="base"
                                    variant="interactive"
                                    maxWidth="100%"
                                  >
                                    <Flex
                                      alignItems="center"
                                      gap={2}
                                      minWidth={0}
                                    >
                                      <Box
                                        as="span"
                                        overflow="hidden"
                                        textOverflow="ellipsis"
                                        whiteSpace="nowrap"
                                        flex={1}
                                        minWidth={0}
                                      >
                                        {`${filters.dateRange[0]} → ${filters.dateRange[1]}`}
                                      </Box>
                                      <FontAwesomeIcon
                                        icon={faXmark}
                                        cursor="pointer"
                                        onClick={() => {
                                          setValue('dateRange', []);
                                          setCountLabel(prev =>
                                            prev === "Today's Exports"
                                              ? 'Total Exports'
                                              : prev
                                          );
                                          setSelectedFilters(prev => ({
                                            ...prev,
                                            dateRange: []
                                          }));
                                        }}
                                      />
                                    </Flex>
                                  </Badge>
                                )}
                              {filtersData?.filters?.map(
                                (filterConfig: any) => {
                                  const filterKey =
                                    filterConfig.key as keyof typeof filters;
                                  if (filterKey === 'dateRange') return null;

                                  const filterValues = filters[filterKey];
                                  if (
                                    !Array.isArray(filterValues) ||
                                    filterValues.length === 0
                                  )
                                    return null;
                                  return filterValues.map(value => {
                                    const valueLabel =
                                      filterConfig.items?.find(
                                        (i: any) => i.value === value
                                      )?.name ?? value;

                                    return (
                                      <Badge
                                        key={`${String(filterKey)}-${value}`}
                                        size="base"
                                        variant="interactive"
                                        maxWidth="100%"
                                      >
                                        <Flex
                                          alignItems="center"
                                          gap={2}
                                          minWidth={0}
                                        >
                                          <Box
                                            as="span"
                                            overflow="hidden"
                                            textOverflow="ellipsis"
                                            whiteSpace="nowrap"
                                            flex={1}
                                            minWidth={0}
                                          >
                                            {`${valueLabel}`}
                                          </Box>
                                          <FontAwesomeIcon
                                            icon={faXmark}
                                            cursor="pointer"
                                            onClick={() => {
                                              const updatedValues =
                                                filterValues.filter(
                                                  v => v !== value
                                                );

                                              setValue(
                                                String(filterKey),
                                                updatedValues
                                              );
                                              setSelectedFilters(prev => ({
                                                ...prev,
                                                [String(filterKey)]:
                                                  updatedValues
                                              }));
                                            }}
                                          />
                                        </Flex>
                                      </Badge>
                                    );
                                  });
                                }
                              )}
                            </>
                          );
                        })()}
                      </Flex>
                    )}
                  </>
                )}
                {ExportHistory && (
                  <Box overflowX="auto" width="100%">
                    <VendorExportHistoryTable
                      paginatedData={ExportHistory}
                      renderBadge={renderBadge}
                      total={total}
                      totalPages={totalPages}
                      page={page}
                      setPage={setPage}
                    />
                  </Box>
                )}
                {selectedJournal && isAdvanceConfigurationModalOpen && (
                  <Modal
                    isOpen={isAdvanceConfigurationModalOpen}
                    onClose={() => setIsAdvanceConfigurationModalOpen(false)}
                    width={{ base: 'calc(100vw - 16px)', md: '720px' } as any}
                    closeButtonProps={{
                      hidden: false
                    }}
                  >
                    <AdvanceConfigurationVendorExport
                      selectedJournal={selectedJournal}
                      setIsOpen={setIsAdvanceConfigurationModalOpen}
                      orgId={orgId}
                    />
                  </Modal>
                )}
              </Flex>
            </>
          ) : (
            <Box mt={5}>
              <NoRecordFound
                title="No journal selected"
                subTitle="Please select a journal to proceed"
              />
            </Box>
          )}
        </Box>
        <Footer />
      </Box>
    </Box>
  );
};

export default VendorExport;
